// Copyright 2025 Imprivata, Inc. All rights reserved.

import { Button, InputBox, Modal, ImprSwitch } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CodingContext, getRSAPublicKey } from '@imprivata-cloud/dataprivacy';
import moment from 'moment';

import { getTemporaryCodeMetadata, imprIdpUsersSave } from '../../../api/portalServices';
import {
  Action,
  type ImprPhoneNumber,
  type ImprUser,
  type ImprUserEmail,
  isAdmin,
  ImprUserRoleValue,
  addAdminRoleTemplate,
  findRole,
  type ImprUserRole,
  buildAddUserRole,
} from '../../../api/types';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import CopyButton from '../../../components/copyButton/copyButton';
import { formatDateInUtcWithTz, Regex } from '../../../utils/utils';

import './UserModal.less';
import { useAssignTempCode } from './hooks/useTempCode';
import { _getUrl } from '../../../api/utils';

enum FormFieldNames {
  Username = 'Username',
  FirstName = 'FirstName',
  LastName = 'LastName',
  Email = 'Email',
  Phone = 'Phone',
  IsAdmin = 'IsAdmin',
  TemporaryAccessCode = 'TemporaryAccessCode',
}

interface FormValues {
  Username: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string;
  IsAdmin: boolean;
  TemporaryAccessCode: string;
}

const TRANSLATION_ROOT = 'portal.settings.identity-users.user-modal';

function buildPayload(values: FormValues, domain: string, editUserData?: ImprUser) {
  const isUserUpdate = !!editUserData?.userId;
  let updatedPhoneNumbers: ImprPhoneNumber[] = [];
  let updatedEmails: ImprUserEmail[] = [];
  let updatedRoles: ImprUserRole[] = [];
  if (isUserUpdate) {
    //email updated
    if (
      editUserData?.emails &&
      editUserData?.emails?.length > 0 &&
      values.Email?.trim() !== editUserData?.emails[0]?.value
    ) {
      updatedEmails = [
        {
          emailId: editUserData?.emails[0]?.emailId,
          value: editUserData?.emails[0]?.value ?? '',
          isPrimary: true,
          action: Action.REMOVE,
        },
        { value: values.Email?.trim(), isPrimary: true, action: Action.ADD },
      ];
    } else if (editUserData?.emails?.length === 0) {
      //handle the case where user is created on setup and does not have an email.
      updatedEmails = [{ value: values.Email?.trim(), isPrimary: true, action: Action.ADD }];
    }
    //phone updated
    if (
      editUserData?.phoneNumbers &&
      editUserData?.phoneNumbers?.length > 0 &&
      values.Phone?.trim() !== editUserData?.phoneNumbers[0]?.value
    ) {
      updatedPhoneNumbers = [
        {
          value: editUserData?.phoneNumbers[0]?.value ?? '',
          isPrimary: true,
          phoneNumberId: editUserData?.phoneNumbers[0]?.phoneNumberId,
          action: Action.REMOVE,
        },
      ];
      if (values.Phone?.trim()) {
        updatedPhoneNumbers?.push({ value: values.Phone?.trim(), isPrimary: true, action: Action.ADD });
      }
    } else if (editUserData?.phoneNumbers?.length === 0) {
      updatedPhoneNumbers = values.Phone?.trim()
        ? [{ value: values.Phone?.trim(), isPrimary: true, action: Action.ADD }]
        : [];
    }
    if (values.IsAdmin !== isAdmin(editUserData)) {
      // Beware of the order, or you may get error "Role value already exists" or "Only one primary role is allowed"
      const currentUserRole = findRole(ImprUserRoleValue.user, editUserData.roles);
      if (currentUserRole) {
        currentUserRole.action = Action.REMOVE;
        updatedRoles.push(currentUserRole);
      }
      const currentAdminRole = findRole(ImprUserRoleValue.admin, editUserData.roles);
      if (currentAdminRole) {
        currentAdminRole.action = Action.REMOVE;
        updatedRoles.push(currentAdminRole, buildAddUserRole(true));
      } else {
        updatedRoles.push(addAdminRoleTemplate, buildAddUserRole(false));
      }
    }
  } else {
    updatedPhoneNumbers = values.Phone ? [{ value: values.Phone?.trim(), isPrimary: true }] : [];
    updatedEmails = [{ value: values.Email?.trim(), isPrimary: true }];
    if (values.IsAdmin) {
      updatedRoles = [addAdminRoleTemplate, buildAddUserRole(false)];
    } else {
      updatedRoles = [buildAddUserRole(true)];
    }
  }

  return {
    user: {
      userId: editUserData?.userId,
      username: `${values.Username?.trim()}@${domain}`,
      firstName: values.FirstName?.trim(),
      lastName: values.LastName?.trim(),
      emails: updatedEmails,
      phoneNumbers: updatedPhoneNumbers,
      roles: updatedRoles,
    },
  };
}

const UserModal = ({
  domain,
  closeModal,
  refetchUsers,
  editUserData,
  adminDeleteDisabled,
  isCurrentUser,
}: {
  domain?: string;
  closeModal: () => void;
  refetchUsers?: () => Promise<unknown>;
  editUserData?: ImprUser;
  adminDeleteDisabled?: boolean;
  isCurrentUser?: boolean;
}) => {
  const { t } = useTranslation();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [form] = Form.useForm<FormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [tempAccessCode, setTempAccessCode] = useState('');
  const { mutate: assignTempCode, isPending } = useAssignTempCode();
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const getTemporaryCodeExpiration = async (userId: string) => {
    try {
      const metadata = await getTemporaryCodeMetadata({ userId });

      const formattedExpire = formatDateInUtcWithTz(metadata.expiresAt) || '';
      setExpiresAt(formattedExpire);
      localStorage.setItem(`tempCodeExpiresAt_${userId}`, formattedExpire);
    } catch (error) {
      console.error('Error fetching temp code metadata:', error);
      emitError(new AppError(error as ApiError));
    }
  };

  useEffect(() => {
    if (!editUserData?.userId) {
      return;
    }
    const key = `tempCodeExpiresAt_${editUserData.userId}`;
    const storedExpire = localStorage.getItem(key);
    if (storedExpire) {
      const expireMoment = moment(storedExpire, 'YYYY-MM-DD HH:mm:ss [UTC]ZZ');
      if (expireMoment.isSameOrBefore(moment())) {
        localStorage.removeItem(key);
        setExpiresAt(null);
      } else {
        setExpiresAt(storedExpire);
      }
    } else {
      setExpiresAt(null);
    }
  }, [editUserData?.userId]);

  const generateAccessCode = async () => {
    const userId = editUserData?.userId;
    if (!userId) {
      console.log('UserId was not found.');
      return;
    }

    try {
      const { metadataUrl } = _getUrl({ metadataUrl: { sub: 'metadata.app' } });
      const publicDev = await getRSAPublicKey(`${metadataUrl}/data-privacy/public.json`);
      const codingCtx = await CodingContext.createContextV2(publicDev.key, publicDev.version);

      assignTempCode(
        { request: { userId }, codingContext: codingCtx },
        {
          onSuccess: async (response) => {
            try {
              const decryptedTempCode = codingCtx.decryptToString(response.tempCode);
              setTempAccessCode(decryptedTempCode);
              form.setFieldValue(FormFieldNames.TemporaryAccessCode, decryptedTempCode);
              await getTemporaryCodeExpiration(userId);
            } catch (decryptError) {
              console.error('Error decrypting temp code:', decryptError);
              emitError(new AppError(decryptError as ApiError));
            }
          },
          onError: (e: unknown) => {
            console.error('Error generating temporary code:', e);
            emitError(new AppError(e as ApiError));
          },
        },
      );
    } catch (err) {
      console.error('Error generating or decrypting access code:', err);
      emitError(new AppError(err as ApiError));
    }
  };

  useEffect(() => {
    if (editUserData) {
      form.setFieldsValue({
        Username: editUserData.username?.split('@')[0],
        FirstName: editUserData.firstName,
        LastName: editUserData.lastName,
        Email: editUserData.emails?.find((email) => email.isPrimary)?.value,
        Phone: editUserData.phoneNumbers?.find((phone) => phone.isPrimary)?.value,
        IsAdmin: isAdmin(editUserData),
      });
    } else {
      form.resetFields();
    }
  }, [editUserData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      clearErrorNotifications();
      const payload = buildPayload(values, domain || '', editUserData);
      await imprIdpUsersSave(payload);
      await new Promise((resolve) => setTimeout(resolve, 1_000));
      await refetchUsers?.();
      closeModal();
    } catch (e) {
      setIsLoading(false);
      emitError(e as AppError);
    }
  };

  return (
    <Modal
      aria-label="user-modal"
      title={
        editUserData
          ? t(`${TRANSLATION_ROOT}.title-edit`, { username: editUserData.username })
          : t(`${TRANSLATION_ROOT}.title-add`)
      }
      className="user-modal"
      open={true}
      width={750}
      onCancel={closeModal}
      footer={null}
    >
      <Form name="UserForm" form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
        <div className="row" style={{ alignItems: 'center' }}>
          <Form.Item name={FormFieldNames.Username} label={t(`${TRANSLATION_ROOT}.input.username`)}>
            <InputBox
              aria-label="username-input"
              autoFocus
              autofocusDelayMs={300}
              minLength={6}
              maxLength={30}
              required
              size="large"
            />
          </Form.Item>
          <span className="post-label">@{domain}</span>
        </div>

        <div className="row">
          <Form.Item name={FormFieldNames.FirstName} label={t(`${TRANSLATION_ROOT}.input.first-name`)}>
            <InputBox size="large" aria-label="first-name-input" />
          </Form.Item>
          <Form.Item name={FormFieldNames.LastName} label={t(`${TRANSLATION_ROOT}.input.last-name`)}>
            <InputBox size="large" aria-label="last-name-input" />
          </Form.Item>
        </div>

        <Form.Item name={FormFieldNames.Email} label={t(`${TRANSLATION_ROOT}.input.email`)}>
          <InputBox aria-label="email-input" maxLength={254} required size="large" type="email" />
        </Form.Item>

        <div className="row">
          <Form.Item
            name={FormFieldNames.Phone}
            label={t(`${TRANSLATION_ROOT}.input.phone`)}
            rules={[{ pattern: Regex.phone, message: 'Valid RFC 3966 phone number required' }]}
          >
            <InputBox minLength={9} maxLength={20} size="large" aria-label="phone-input" />
          </Form.Item>
          <div />
          {/* this div is for the visual formatting */}
        </div>

        {/* Temporary access code */}
        {!!editUserData && (
          <div className="row">
            <Form.Item name={FormFieldNames.TemporaryAccessCode} label={t(`${TRANSLATION_ROOT}.input.temp-code`)}>
              <div className="temp-access-row">
                <InputBox aria-label="temp-access-code-input" size="large" readOnly value={tempAccessCode} />
                <CopyButton
                  textToCopy={form.getFieldValue(FormFieldNames.TemporaryAccessCode)}
                  disabled={!form.getFieldValue(FormFieldNames.TemporaryAccessCode)}
                  className="copy-temp-code"
                  dynamicLabel={{
                    initialLabel: t('common.copy'),
                    labelAfterClick: t('common.copied'),
                    dynamicLabelDuration: 2000,
                  }}
                />
                <Button label={t('common.generate-new')} type="text" onClick={generateAccessCode} loading={isPending} />
              </div>
              <div className="temp-access-description">
                {expiresAt ? t(`${TRANSLATION_ROOT}.input.temp-code-description`, { expiresAt }) : ''}
              </div>
            </Form.Item>
          </div>
        )}

        {/* Switch: User is an administrator */}
        <div className="row">
          <Form.Item name={FormFieldNames.IsAdmin} valuePropName="checked">
            <ImprSwitch
              switchLabel={{
                labelOn: t(`${TRANSLATION_ROOT}.input.isAdmin`),
                labelOff: t(`${TRANSLATION_ROOT}.input.isAdmin`),
              }}
              disabled={isAdmin(editUserData) && (adminDeleteDisabled || isCurrentUser)}
            />
          </Form.Item>
        </div>

        {/* Footer: Cancel and Save buttons */}
        <div className="form-footer-right">
          <Button aria-label="button-close" onClick={closeModal} label={t('common.close')} />
          <Form.Item shouldUpdate>
            {(form) => {
              const values = form.getFieldsValue();
              const isSubmitEnabled = !!(values.Username?.trim() && values.Email?.trim());
              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  label={t('common.save')}
                  disabled={isLoading || !isSubmitEnabled}
                />
              );
            }}
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default UserModal;
